// RecoverForm.tsx
import React, { useState } from 'react';
import { Alert, Button, Form, Input, Typography } from 'antd';
import { ENDPOINTS } from '../../helpers/api/apiConfig'; // Adjust the import path as necessary
import '../../style/LoginForm.css';

interface RecoverFormProps {
    onBackToLogin: () => void;
}

const RecoverForm: React.FC<RecoverFormProps> = ({ onBackToLogin }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [form] = Form.useForm();
    const { Title, Text } = Typography;

    const handleSubmit = async (values: { email: string }) => {
        setError(null);
        setSuccess(null);
        setIsSubmitting(true);

        try {
            const response = await fetch(ENDPOINTS.FORGOT_PASSWORD, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: values.email }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to send recovery email' }));
                throw new Error(errorData?.message || 'Failed to send recovery email');
            }

            setSuccess('If an account exists for this email, a password reset link has been sent.');
        } catch (error: any) {
            console.error('Failed to send recovery email:', error);
            setError(error?.message || 'Failed to send recovery email. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            className="login-form recover-form auth-antd-form"
            onFinish={handleSubmit}
            autoComplete="on"
        >
            <div className="auth-form-header">
                <Title level={2}>Recover Password</Title>
                <Text className="auth-form-subtitle">
                    Enter your email and we will send a reset link.
                </Text>
            </div>

            {error && (
                <Alert
                    type="error"
                    showIcon
                    message={error}
                    className="auth-form-alert"
                />
            )}

            {success && (
                <Alert
                    type="success"
                    showIcon
                    message={success}
                    className="auth-form-alert"
                />
            )}

            <Form.Item
                name="email"
                rules={[
                    { required: true, message: 'Please enter your email.' },
                    { type: 'email', message: 'Please enter a valid email address.' },
                ]}
                className="auth-form-item"
            >
                <Input
                    placeholder="Email address"
                    autoComplete="email"
                    size="large"
                    disabled={isSubmitting}
                />
            </Form.Item>

            <Form.Item className="auth-form-item auth-submit-item">
                <Button
                    className="button auth-submit-button"
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    block
                    size="large"
                >
                    {isSubmitting ? 'Sending...' : 'Send Recovery Email'}
                </Button>
            </Form.Item>

            <div className="auth-form-footer">
                <Button
                    type="link"
                    className="link-like-button"
                    onClick={onBackToLogin}
                    disabled={isSubmitting}
                >
                    Back to Login
                </Button>
            </div>
        </Form>
    );
};

export default RecoverForm;
